import { FC, useMemo } from 'react';
import { Stack } from '@mui/material';
import type { CutsheetCatalog, GlobalSpecQuestion } from '../../../types';
import { QuestionItemCard } from './catalog-item-card';
import { DropZone } from './drop-zone';

export interface GlobalSpecContentProps {
  question: GlobalSpecQuestion;
  catalog: CutsheetCatalog;
  canAccept: boolean | null;
  draggingType: string | null;
  onAddItems?: (questionId: string, itemIds: string[]) => void;
  onRemoveItem?: (questionId: string, itemId: string) => void;
  onAddClick: () => void;
}

/**
 * Content panel for a GlobalSpec question.
 *
 * Shows the currently-selected specification as a card and, when none is
 * selected, a droppable zone for setting one via drag or click.
 * @param root0
 * @param root0.question
 * @param root0.catalog
 * @param root0.canAccept
 * @param root0.draggingType
 * @param root0.onAddItems
 * @param root0.onRemoveItem
 * @param root0.onAddClick
 */
export const GlobalSpecContent: FC<GlobalSpecContentProps> = ({
  question,
  catalog,
  canAccept,
  draggingType,
  onAddItems,
  onRemoveItem,
  onAddClick,
}) => {
  const globalSpecItem = useMemo(
    () =>
      question.specificationId
        ? (catalog.specifications.find(
            (s) => s.id === question.specificationId,
          ) ?? null)
        : null,
    [question.specificationId, catalog.specifications],
  );

  return (
    <Stack spacing={1}>
      {/* Currently-selected specification */}
      {globalSpecItem && (
        <QuestionItemCard
          title={globalSpecItem.title}
          description={globalSpecItem.description}
          imageUrl={globalSpecItem.imageUrl}
          accentColor="primary.main"
          onRemove={
            onRemoveItem
              ? () => onRemoveItem(question.id, globalSpecItem.id)
              : undefined
          }
        />
      )}

      {/* Dropzone — only visible when no spec is selected yet */}
      {onAddItems && !globalSpecItem && (
        <DropZone
          droppableId={`question-dropzone-${question.id}`}
          droppableData={{ type: 'question-dropzone', questionId: question.id }}
          canAccept={canAccept}
          draggingType={draggingType}
          incompatibleMessage="This question only accepts specifications"
          placeholder="Click or Drag a specification here."
          onClick={onAddClick}
        />
      )}
    </Stack>
  );
};
